import React from 'react'
import { ChakraProvider } from '@chakra-ui/react'
import { Font } from '@react-pdf/renderer'

import '@fontsource/nunito/200.css'
import '@fontsource/nunito/300.css'
import '@fontsource/nunito/400.css'
import '@fontsource/nunito/500.css'
import '@fontsource/nunito/600.css'
import '@fontsource/nunito/700.css'
import '@fontsource/nunito/800.css'
import '@fontsource/nunito/900.css'
import '@fontsource/inter/100.css'
import '@fontsource/inter/200.css'
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/800.css'
import '@fontsource/inter/900.css'
import '@fontsource/lilita-one'
import '@fontsource/dm-serif-display'
import '@fontsource/poppins/100.css'
import '@fontsource/poppins/200.css'
import '@fontsource/poppins/300.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
import '@fontsource/poppins/800.css'
import '@fontsource/poppins/900.css'

// @ts-ignore
import Inter400 from '@fontsource/inter/files/inter-all-400-normal.woff'
// @ts-ignore
import Inter500 from '@fontsource/inter/files/inter-all-500-normal.woff'
// @ts-ignore
import Inter600 from '@fontsource/inter/files/inter-all-600-normal.woff'
// @ts-ignore
import Inter700 from '@fontsource/inter/files/inter-all-700-normal.woff'
// @ts-ignore
import Inter800 from '@fontsource/inter/files/inter-all-800-normal.woff'
// @ts-ignore
import Inter900 from '@fontsource/inter/files/inter-all-900-normal.woff'

import 'react-image-crop/dist/ReactCrop.css'

import theme from '../@chakra-ui/gatsby-plugin/theme'
import { ApolloProvider } from '@apollo/client'

import { UserProvider } from '~config/user-context'
import { ClinicProvider } from '~contexts/clinic-context'
import { PendingAppointmentsProvider } from '~contexts/pending-appointments-context'
import { LoadingProvider } from '~config/loading-context'
import SyncProvider from '~contexts/sync-context'
import UnreadMessagesProvider from '~contexts/unread-messages-provider'
import { ActionItemProvider } from '~contexts/action-item-provider'
import { ReportTemplatesProvider } from '~contexts/report-templates-provider'
import { KpiGroupsProvider } from '~components/enterprise/kpis/kpi-groups-provider'
import { InitiativesProvider } from '~components/enterprise/initiatives/initiatives-provider'
import ConditionalProvider from '~components/shared/wrappers/conditional-provider'
import UnreadInternalChatsProvider from '~contexts/unread-internal-chats-provider'
import UserNotificationsProvider from '~contexts/notifications-provider'
import { ClientOnlyGoogleMapsProvider } from '~components/shared/providers/client-only-google-maps-provider'
import OpenChatsProvider from '~components/whiteboard/open-chats-provider'
import OpenTeamChatsProvider from '~components/team-chat/popovers/open-team-chats-provider'
import PinnedPagesProvider from '~components/shared/layout/sidebar/new-sidebar/pinned-pages-provider'

import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { InactivityProvider } from '~config/inactivity-timeout-context'
import { getApolloClient } from '~utils/apollo-client'

interface Props {
    element: React.ReactNode
}

let didRegisterPdfFonts = false

function AppProviders({ children }: { children: React.ReactNode }) {
    const client = getApolloClient()

    return (
        <DndProvider backend={HTML5Backend}>
            <ApolloProvider client={client}>
                <ChakraProvider theme={theme}>
                    <LoadingProvider>
                        <UserProvider>
                            <ClinicProvider>
                                <ClientOnlyGoogleMapsProvider>
                                    <UnreadMessagesProvider>
                                        <InactivityProvider>
                                            <SyncProvider>
                                                <ReportTemplatesProvider>
                                                    <KpiGroupsProvider>
                                                        <InitiativesProvider>
                                                            <PendingAppointmentsProvider>
                                                                <ActionItemProvider>
                                                                    <UnreadInternalChatsProvider>
                                                                        <UserNotificationsProvider>
                                                                            <OpenChatsProvider>
                                                                                <OpenTeamChatsProvider>
                                                                                    <PinnedPagesProvider>
                                                                                        {children}
                                                                                    </PinnedPagesProvider>
                                                                                </OpenTeamChatsProvider>
                                                                            </OpenChatsProvider>
                                                                        </UserNotificationsProvider>
                                                                    </UnreadInternalChatsProvider>
                                                                </ActionItemProvider>
                                                            </PendingAppointmentsProvider>
                                                        </InitiativesProvider>
                                                    </KpiGroupsProvider>
                                                </ReportTemplatesProvider>
                                            </SyncProvider>
                                        </InactivityProvider>
                                    </UnreadMessagesProvider>
                                </ClientOnlyGoogleMapsProvider>
                            </ClinicProvider>
                        </UserProvider>
                    </LoadingProvider>
                </ChakraProvider>
            </ApolloProvider>
        </DndProvider>
    )
}

const WrapRootElement: React.FC<Props> = ({ element }) => {
    if (!didRegisterPdfFonts) {
        didRegisterPdfFonts = true
        Font?.register({
            family: 'Inter',
            fonts: [
                {
                    src: Inter400,
                    fontWeight: 400,
                },
                {
                    src: Inter500,
                    fontWeight: 500,
                },
                {
                    src: Inter600,
                    fontWeight: 600,
                },
                {
                    src: Inter700,
                    fontWeight: 700,
                },
                {
                    src: Inter800,
                    fontWeight: 800,
                },
                {
                    src: Inter900,
                    fontWeight: 900,
                },
            ],
        })
    }

    return <AppProviders>{element}</AppProviders>
}

export default WrapRootElement
